'use client'

import { type ComponentPropsWithoutRef, forwardRef, useState } from 'react'
import clsx from 'clsx'
import { motion, type Transition } from 'motion/react'
import { ChevronIconSmall } from '../../icons/ChevronIconSmall'
import { xStack, yStack } from '../../patterns/stack'
import { framerTransitions } from '../../theme/transitions'
import { Collapsible } from '../Collapsible/Collapsible'
import { PerilColorIcon } from '../PerilColorIcon/PerilColorIcon'
import { PerilCoverageList } from '../PerilCoverageList/PerilCoverageList'
import { Text } from '../Text/Text'
import { sprinkles } from '../../theme/sprinkles.css'
import { tokens } from '../../theme/tokens.css'
import { cell, collapsibleContent, collapsibleTrigger, triggerIcon } from './ComparisonTable.css'

type TitleDataCellProps = Omit<ComponentPropsWithoutRef<'th'>, 'children'> & {
  title: string
  description?: string
  perilColor?: string | null
  coverageList?: Array<string>
}

export function TitleDataCell({
  className,
  title,
  description,
  perilColor,
  coverageList,
  ...props
}: TitleDataCellProps) {
  const [open, setOpen] = useState(false)

  const cellTitle = (
    <div className={xStack({ gap: 'xs', alignItems: 'center' })}>
      {perilColor && <PerilColorIcon color={perilColor} />}
      <Text as="span" size={{ _: 'xs', md: 'md' }} color="textPrimary">
        {title}
        {description && (
          // Size is controlled via CSS ('triggerIcon' class)
          <ChevronIconSmall className={triggerIcon} color={tokens.colors.fills.opaque.tertiary} />
        )}
      </Text>
    </div>
  )
  const children = description ? (
    <Collapsible.Root open={open} onOpenChange={setOpen}>
      <Collapsible.Trigger className={collapsibleTrigger}>{cellTitle}</Collapsible.Trigger>
      <Collapsible.Content
        className={clsx(collapsibleContent, perilColor && sprinkles({ pl: 'xl' }))}
        asChild={true}
        // SEO: Make sure content gets mounted despite being collapsed
        forceMount={true}
      >
        <AnimatedTitleDataCellContent open={open} description={description} coverageList={coverageList} />
      </Collapsible.Content>
    </Collapsible.Root>
  ) : (
    cellTitle
  )

  return (
    <th scope="row" className={clsx(cell, className)} {...props}>
      {children}
    </th>
  )
}

type AnimatedTitleDataCellProps = { open: boolean } & Pick<
  TitleDataCellProps,
  'description' | 'coverageList' | 'className'
>

const ANIMATE_TRANSITION: Transition = {
  ...framerTransitions.easeInOutCubic,
  duration: framerTransitions.defaultDuration,
}

const AnimatedTitleDataCellContent = forwardRef<HTMLDivElement, AnimatedTitleDataCellProps>(
  ({ open, description, coverageList, className }, ref) => {
    return (
      <motion.div
        ref={ref}
        className={clsx(sprinkles({ overflow: 'hidden' }), className)}
        initial={false}
        transition={ANIMATE_TRANSITION}
        variants={{
          opened: {
            height: 'auto',
          },
          closed: {
            height: 0,
          },
        }}
        animate={open ? 'opened' : 'closed'}
      >
        <div className={yStack({ gap: 'md', py: 'xxs' })}>
          <Text as="p" size={{ _: 'xs', lg: 'md' }} color="textSecondary">
            {description}
          </Text>
          {coverageList && (
            <PerilCoverageList
              className={sprinkles({ fontSize: { _: 'xs', lg: 'md' } })}
              items={coverageList}
              variant="secondary"
            />
          )}
        </div>
      </motion.div>
    )
  },
)
AnimatedTitleDataCellContent.displayName = 'AnimatedTitleDataCell'
